import {
    ApprovalBudgetGet,
    ApprovalExpenseGet,
    ApprovalInvoiceGet,
    ApprovalPurchaseOrderGet,
    Decimal,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { store } from "~/core/store";
import { dateFromApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    ApprovalBudget,
    ApprovalEntityFilter,
    ApprovalExpense,
    ApprovalInvoice,
    ApprovalParentFilter,
    ApprovalPurchaseOrder,
    ApproveBudgetParams,
    ApproveEntityType,
    ApproveFilter,
    ApproveParams,
    ApprovePrivateFilter,
    ApprovePurchaseOrderParams,
} from "./interfaces";
import {
    getEntityId,
    getFilterKey,
    getPrivateFilterKey,
    splitCompanies,
} from "./utils";

export const makeApproval = ({ connection }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<
        | ApprovalExpense
        | ApprovalInvoice
        | ApprovalBudget
        | ApprovalPurchaseOrder
    >();
    const loadingStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedStore: Record<string, ReadonlySignal<boolean>> = {};
    const loadedAllStore: Record<string, ReadonlySignal<boolean>> = {};
    const {
        items: expenses,
        listen: listenExpenses,
        loading: loadingExpenses,
        loaded: loadedExpenses,
    } = createListController<
        ApprovalExpenseGet,
        ApprovalExpense,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.expense.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("expense", e),
        view: (item) => ({
            ...item,
            type: "expense",
            _id: item.id,
            id: getEntityId("expense", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalExpense",
    });

    const {
        items: invoices,
        listen: listenInvoices,
        loading: loadingInvoices,
        loaded: loadedInvoices,
    } = createListController<
        ApprovalInvoiceGet,
        ApprovalInvoice,
        ApprovePrivateFilter
    >({
        connection,
        list: (con, { status, companies }) =>
            con.approve.invoice.approval.itemsBatch({
                status,
                company: companies,
            }),
        getKey: getPrivateFilterKey,
        getId: (e) => getEntityId("invoice", e),
        view: (item) => ({
            ...item,
            type: "invoice",
            _id: item.id,
            id: getEntityId("invoice", item),
            date: dateFromApi(item.date),
        }),
        emit,
        name: "approvalInvoice",
    });

    const getParentFilterKey = (filter: ApprovalParentFilter) =>
        `parent:${filter.parent.toString()}|${filter.status ?? ""}`;

    const {
        items: allBudgets,
        listen: listenBudgets,
        loading: loadingBudgets,
        loaded: loadedBudgets,
    } = createListController<
        ApprovalBudgetGet,
        ApprovalBudget,
        ApprovalEntityFilter
    >({
        connection,
        list: (con, filter) =>
            "parent" in filter
                ? con.approve.budget.approval.items({
                      company: filter.company,
                      parent: filter.parent,
                  })
                : con.approve.budget.approval.itemsBatch({
                      status: filter.status,
                      company: filter.companies,
                  }),
        getKey: (filter) =>
            "parent" in filter
                ? getParentFilterKey(filter)
                : getPrivateFilterKey(filter),
        getId: (e) => getEntityId("budget", e),
        view: (item) => ({
            ...item,
            type: "budget",
            _id: item.id,
            id: getEntityId("budget", item),
            date: dateFromApi(item.budget_date),
        }),
        emit,
        name: "approvalBudget",
    });

    const {
        items: allPurchaseOrders,
        listen: listenPurchaseOrders,
        loading: loadingPurchaseOrders,
        loaded: loadedPurchaseOrders,
    } = createListController<
        ApprovalPurchaseOrderGet,
        ApprovalPurchaseOrder,
        ApprovalEntityFilter
    >({
        connection,
        list: (con, filter) =>
            "parent" in filter
                ? con.approve.purchaseorder.approval.items({
                      company: filter.company,
                      parent: filter.parent,
                  })
                : con.approve.purchaseorder.approval.itemsBatch({
                      status: filter.status,
                      company: filter.companies,
                  }),
        getKey: (filter) =>
            "parent" in filter
                ? getParentFilterKey(filter)
                : getPrivateFilterKey(filter),
        getId: (e) => getEntityId("purchaseorder", e),
        view: (item) => ({
            ...item,
            type: "purchaseorder",
            _id: item.id,
            id: getEntityId("purchaseorder", item),
            date: dateFromApi(item.purchaseorder_date),
        }),
        emit,
        name: "approvalPurchaseOrder",
    });

    const budgets = computed(() => allBudgets.value.filter((b) => !b.archived));
    const purchaseOrders = computed(() =>
        allPurchaseOrders.value.filter((po) => !po.archived),
    );

    const serviceCheck = () =>
        [
            store.api.service.enabledPeek("ApprovalInvoice", "List"),
            store.api.service.enabledPeek("ApprovalExpense", "List"),
            store.api.service.enabledPeek("ApprovalBudget", "List"),
            store.api.service.enabledPeek("ApprovalPurchaseOrder", "List"),
        ] as const;

    const myExpenses = computed(() =>
        expenses.value.filter(
            (e) => e.user === connection.value.user.preferred_username,
        ),
    );
    const myInvoices = computed(() =>
        invoices.value.filter(
            (i) => i.user === connection.value.user.preferred_username,
        ),
    );
    const myBudgets = computed(() =>
        budgets.value.filter((b) =>
            b.approver.equals(connection.value.user.user_id),
        ),
    );
    const myPurchaseOrders = computed(() =>
        purchaseOrders.value.filter((p) =>
            p.approver.equals(connection.value.user.user_id),
        ),
    );
    const myAll = computed(() => [
        ...myExpenses.value,
        ...myInvoices.value,
        ...myBudgets.value,
        ...myPurchaseOrders.value,
    ]);
    const mine = {
        expenses: myExpenses,
        invoices: myInvoices,
        budgets: myBudgets,
        purchaseOrders: myPurchaseOrders,
        all: myAll,
    };
    const all = computed(() => [
        ...expenses.value,
        ...invoices.value,
        ...budgets.value,
        ...purchaseOrders.value,
    ]);

    const listen = (
        filterInvoice: ApprovePrivateFilter,
        filterExpense: ApprovePrivateFilter,
        filterBudget: ApprovePrivateFilter,
        filterPurchaseOrder: ApprovePrivateFilter,
        types: ApproveEntityType[] = [
            "expense",
            "invoice",
            "budget",
            "purchaseorder",
        ],
    ) => {
        const cbs: (() => void)[] = [];
        const [invoiceEnabled, expenseEnabled, budgetEnabled, poEnabled] =
            serviceCheck();
        const inv =
            invoiceEnabled && invoiceEnabled && types.includes("invoice");
        const exp = expenseEnabled && types.includes("expense");
        const bud = budgetEnabled && types.includes("budget");
        const po = poEnabled && types.includes("purchaseorder");

        if (inv) {
            cbs.push(listenInvoices(filterInvoice));
        }

        if (exp) {
            cbs.push(listenExpenses(filterExpense));
        }

        if (bud) {
            cbs.push(listenBudgets(filterBudget));
        }

        if (po) {
            cbs.push(listenPurchaseOrders(filterPurchaseOrder));
        }

        return () => {
            cbs.forEach((cb) => cb());
        };
    };

    const approveInvoice = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.invoice.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const approveExpense = ({
        id,
        parent,
        company,
        amount,
        comment,
        kind,
    }: ApproveParams) =>
        connection.value.approve.expense.approval.approve({
            id,
            parent,
            company,
            amount: Decimal.from(amount),
            comment,
            kind,
        });

    const approveBudget = ({
        id,
        company,
        comment,
        kind,
    }: ApproveBudgetParams) =>
        connection.value.approve.budget.approval.approve({
            id,
            company,
            comment,
            kind,
        });

    const approvePurchaseOrder = ({
        id,
        company,
        comment,
        kind,
    }: ApprovePurchaseOrderParams) =>
        connection.value.approve.purchaseorder.approval.approve({
            id,
            company,
            comment,
            kind,
        });

    const loading = (filter: ApproveFilter, type: ApproveEntityType) => {
        const key = type + getFilterKey(filter);

        if (!(key in loadingStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadingInvoices({ status, companies: invoiceCompanies }),
                );

                loadingStore[key] = computed(() =>
                    inv.map((i) => i.value).some((v) => v),
                );
            } else if (type === "expense") {
                const exp = filter.statuses.map((status) =>
                    loadingExpenses({ status, companies: expenseCompanies }),
                );

                loadingStore[key] = computed(() =>
                    exp.map((e) => e.value).some((v) => v),
                );
            } else if (type === "budget") {
                const bud = filter.statuses.map((status) =>
                    loadingBudgets({ status, companies: filter.companies }),
                );

                loadingStore[key] = computed(() =>
                    bud.map((b) => b.value).some((v) => v),
                );
            } else if (type === "purchaseorder") {
                const po = filter.statuses.map((status) =>
                    loadingPurchaseOrders({
                        status,
                        companies: filter.companies,
                    }),
                );

                loadingStore[key] = computed(() =>
                    po.map((p) => p.value).some((v) => v),
                );
            }
        }

        return loadingStore[key];
    };

    const loaded = (
        filter: ApproveFilter,
        type: ApproveEntityType,
        api = false,
    ) => {
        const key = type + getFilterKey(filter, api);

        if (!(key in loadedStore)) {
            const [invoiceCompanies, expenseCompanies] = splitCompanies(
                filter.companies,
            );

            if (type === "invoice") {
                const inv = filter.statuses.map((status) =>
                    loadedInvoices(
                        { status, companies: invoiceCompanies },
                        api,
                    ),
                );

                loadedStore[key] = computed(() =>
                    inv.map((i) => i.value).every((v) => v),
                );
            } else if (type === "expense") {
                const exp = filter.statuses.map((status) =>
                    loadedExpenses(
                        { status, companies: expenseCompanies },
                        api,
                    ),
                );

                loadedStore[key] = computed(() =>
                    exp.map((e) => e.value).every((v) => v),
                );
            } else if (type === "budget") {
                const bud = filter.statuses.map((status) =>
                    loadedBudgets({ status, companies: filter.companies }, api),
                );

                loadedStore[key] = computed(() =>
                    bud.map((b) => b.value).every((v) => v),
                );
            } else if (type === "purchaseorder") {
                const po = filter.statuses.map((status) =>
                    loadedPurchaseOrders(
                        { status, companies: filter.companies },
                        api,
                    ),
                );

                loadedStore[key] = computed(() =>
                    po.map((p) => p.value).every((v) => v),
                );
            }
        }

        return loadedStore[key];
    };

    const loadedAll = (filter: ApproveFilter, api = false) => {
        const key = getFilterKey(filter, api);

        if (!(key in loadedAllStore)) {
            const [invoiceEnabled, expenseEnabled, budgetEnabled, poEnabled] =
                serviceCheck();
            const signals: ReadonlySignal<boolean>[] = [];

            if (invoiceEnabled) {
                signals.push(loaded(filter, "invoice", api));
            }

            if (expenseEnabled) {
                signals.push(loaded(filter, "expense", api));
            }

            if (budgetEnabled) {
                signals.push(loaded(filter, "budget", api));
            }

            if (poEnabled) {
                signals.push(loaded(filter, "purchaseorder", api));
            }

            loadedAllStore[key] = computed(() =>
                signals.map((s) => s.value).every((v) => v),
            );
        }

        return loadedAllStore[key];
    };

    const approve = (
        params: ApproveParams | ApproveBudgetParams,
        type: Exclude<ApproveEntityType, "budget" | "purchaseorder">,
    ) =>
        type === "invoice"
            ? approveInvoice(params as ApproveParams)
            : approveExpense(params as ApproveParams);

    return {
        expenses,
        invoices,
        budgets,
        purchaseOrders,
        all,
        mine,
        listen,
        listenBudgets,
        listenPurchaseOrders,
        loading,
        loaded,
        loadedAll,
        approve,
        approveBudget,
        approvePurchaseOrder,
        on,
        off,
    };
};
